import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, showErrorToast, showSuccessToast } from '@/components';
import {
  resolveInvoice,
  disputeInvoice,
  INVOICE_DISPUTE_REASONS,
  InvoiceDisputeReason,
} from '@/services/api/invoices';

export type InvoiceAction = 'resolve' | 'dispute';

interface InvoiceActionModalProps {
  action: InvoiceAction | null;
  invoiceId: string;
  // Header context line — invoice number + outlet, e.g. "INV-001 — Apotek Sehat".
  subtitle: string;
  // Acting user's email, forwarded to the gateway as `updatedBy`.
  updatedBy: string;
  onClose: () => void;
  onDone: () => void; // refetch list/summary + reload the drawer
}

const labelCls = 'mb-1.5 block text-xs font-semibold text-text-strong-950';
const fieldCls =
  'w-full rounded-md border border-stroke-sub-300 bg-bg-white-0 px-3 py-2 text-sm text-text-strong-950 placeholder:text-text-soft-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary';

const NOTE_MAX = 280;

const InvoiceActionModal: React.FC<InvoiceActionModalProps> = ({
  action,
  invoiceId,
  subtitle,
  updatedBy,
  onClose,
  onDone,
}) => {
  const [reason, setReason] = useState<InvoiceDisputeReason | ''>('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset fields whenever the modal (re)opens for an action.
  useEffect(() => {
    if (action) {
      setReason('');
      setNote('');
      setSubmitting(false);
    }
  }, [action]);

  if (!action) return null;

  const isDispute = action === 'dispute';
  // Dispute requires reason + note; resolve needs nothing.
  const canSubmit = isDispute ? !!reason && note.trim().length > 0 : true;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      if (isDispute) {
        await disputeInvoice(invoiceId, {
          reason: reason as InvoiceDisputeReason,
          note: note.trim(),
          updatedBy,
        });
        showSuccessToast('Invoice disputed', 'Invoice berhasil di-dispute.');
      } else {
        await resolveInvoice(invoiceId, {
          note: note.trim() || null,
          updatedBy,
        });
        showSuccessToast('Invoice resolved', 'Invoice berhasil di-resolve.');
      }
      onDone();
      onClose();
    } catch (err) {
      showErrorToast(
        isDispute ? 'Gagal dispute' : 'Gagal resolve',
        err instanceof Error ? err.message : 'Terjadi kesalahan.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[60] bg-bg-strong-950/40"
        aria-hidden="true"
        onClick={submitting ? undefined : onClose}
      />

      {/* Centered modal */}
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-xl bg-bg-white-0 shadow-card-lg">
          {/* Header */}
          <div className="flex items-start gap-3 border-b border-stroke-soft-200 px-6 py-4">
            <span
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                isDispute
                  ? 'bg-error-lighter text-error-base'
                  : 'bg-success-lighter text-success-base'
              }`}
            >
              <i
                className={`text-xl ${
                  isDispute ? 'ri-error-warning-line' : 'ri-checkbox-circle-line'
                }`}
              />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold text-text-strong-950">
                {isDispute ? 'Dispute invoice' : 'Resolve invoice'}
              </h2>
              <p className="mt-0.5 truncate text-xs text-text-sub-600">
                {subtitle}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="text-text-soft-400 hover:text-text-sub-600 disabled:opacity-50"
              aria-label="Close"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-5">
            {isDispute && (
              <div className="mb-4">
                <label className={labelCls}>
                  Reason <span className="text-error-base">*</span>
                </label>
                <select
                  className={fieldCls}
                  value={reason}
                  onChange={(e) =>
                    setReason(e.target.value as InvoiceDisputeReason | '')
                  }
                >
                  <option value="">Pilih alasan…</option>
                  {INVOICE_DISPUTE_REASONS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className={labelCls}>
                Notes{' '}
                {isDispute ? (
                  <span className="text-error-base">*</span>
                ) : (
                  <span className="font-normal text-text-soft-400">
                    (opsional)
                  </span>
                )}
              </label>
              <textarea
                rows={3}
                maxLength={NOTE_MAX}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={
                  isDispute
                    ? 'Jelaskan masalahnya…'
                    : 'Tambahkan catatan bila perlu…'
                }
                className={`${fieldCls} resize-none`}
              />
              <div className="mt-1 text-right text-[11px] text-text-soft-400">
                {note.length}/{NOTE_MAX}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 border-t border-stroke-soft-200 px-6 py-4">
            <Button variant="white" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || !canSubmit}
              className={`inline-flex items-center justify-center rounded-md px-4 py-2.5 text-sm font-semibold text-static-white transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                isDispute
                  ? 'bg-error-base hover:bg-error-dark'
                  : 'bg-success-base hover:bg-success-dark'
              }`}
            >
              {submitting ? (
                <>
                  <i className="ri-loader-4-line mr-1.5 animate-spin" />
                  Menyimpan…
                </>
              ) : isDispute ? (
                'Mark disputed'
              ) : (
                'Mark resolved'
              )}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default InvoiceActionModal;
